import { findIndex, intersection, map } from "underscore";
import { QueryClient } from "@tanstack/react-query";

import { UserRecordActivityPayload } from "../components/Records/References/ReferencesComponents/SelectableReferences/useUpdateUserRecordActivity";
import { Module } from "../types/TenantInterfaces";
import FieldUtils from "./FieldUtils";

export const GetRecordName = (mod: Module, record: Record<string, any>) => {
  if (!record || !mod) return record?._RecordName ?? null;

  // Subitem support
  if (record?._isSubitem) {
    const subitemField = mod.Fields.find(
      (f) => f.APIName === record?._SubitemFieldAPIName
    );

    if (!subitemField) return null;

    // @ts-ignore
    return record?.[subitemField?.SubitemRecordNameField];
  }

  const keyRecordName = mod?.Templates?.RecordListView?.RecordNameField;
  const noRecordName = mod?.Templates?.RecordListView?.RecordNameEmpty;

  if (keyRecordName) {
    return record?.[keyRecordName] || record?._RecordName || noRecordName;
  }

  return record?._RecordName;
};

export const openRecords = (
  tenantID: string,
  moduleAPIName: string,
  records: any[]
) => {
  const getUrl = (id: string) =>
    `/clouds/${tenantID}/records/${moduleAPIName}/details/${id}/Summary`;

  for (const record of records) {
    const url = getUrl(record.id);

    window.open(url, "_blank", "noopener,noreferrer");
  }
};

export const getMetadataFieldInfo = (mod: Module) => {
  const metadataField = mod?.Templates?.RecordDetailView?.MetadataField ?? null;

  if (!metadataField || !mod?.Fields) return [null, null];

  const foundField = mod?.Fields.find(
    (field: any) => field.APIName === metadataField
  );

  if (!foundField) return [null, null];

  return [metadataField, foundField?.FieldType];
};

export const getMetadataFromRecord = (
  record: any,
  key: string | null,
  fieldType: string | null
) => {
  if (!key || !fieldType) return null;

  if (!record?.[key]) return null;

  if (["Email", "Phone", "URL"].includes(fieldType)) {
    const primaryValue = FieldUtils.getPrimaryOrFirstValue(
      record[key],
      fieldType
    );

    return primaryValue;
  }

  if (fieldType === "Text") {
    return record[key] || null;
  }

  return null;
};

export const getRecordImageInfo = (mod: any) => {
  const imageField = mod?.Templates?.RecordDetailView?.ImageField ?? null;

  if (!imageField || !mod?.Fields) return [null, null];

  const foundField = mod?.Fields.find(
    (field: any) => field?.APIName === imageField
  );

  return [imageField, foundField?.Presentation];
};

const handleMultiRefGroup = (
  newRecords: any[],
  groupBy: string,
  record: any,
  moduleAPIName: string
) => {
  // To make things easy, first remove all instances of this record
  for (const recordGroup of newRecords) {
    recordGroup.GroupBy.records = recordGroup?.GroupBy?.records.filter(
      (r: any) => r._id !== record.id
    );
  }

  for (const r of record?.[groupBy]) {
    const i = findIndex(newRecords, (g) => {
      return g?.GroupBy?.[groupBy]?.id === r?.id;
    });

    if (i === -1) {
      newRecords.push({
        GroupBy: {
          id: groupBy,
          Count: 1,
          records: [record],
          [groupBy]: r,
        },
        _ModuleAPIName: moduleAPIName,
      });

      continue;
    }

    const groupRecords = newRecords[i]?.GroupBy?.records;

    if (!groupRecords?.map((r: any) => r._id).includes(r.id)) {
      groupRecords.push(record);
    }
  }
};

/**
 * findGroupForRecord - Helper function for handleUpdatedRecords. Used in Grouped Queries
 *                      Finds a group for the record to be placed in using the groupBy field.
 *                      If no group exists, it creates a new one for the record.
 *
 *                      Handles various
 */
const findGroupForRecord = (
  newRecords: any[],
  groupBy: string,
  record: any,
  moduleAPIName: string
) => {
  let i;
  const groupByValue = record?.[groupBy];
  const isArray = Array.isArray(groupByValue);

  if (groupByValue?.id) {
    // SingleReference
    i = findIndex(newRecords, (g) => {
      return g?.GroupBy?.[groupBy]?.id === groupByValue?.id;
    });
  } else if (isArray && groupByValue?.[0]?.id) {
    return handleMultiRefGroup(newRecords, groupBy, record, moduleAPIName);
  } else if (typeof groupByValue === "string") {
    // Single Select
    i = findIndex(newRecords, (g) => {
      return g?.GroupBy?.[groupBy] === groupByValue;
    });
  } else if (isArray && typeof groupByValue?.[0] === "string") {
    // Multi Select
    i = findIndex(newRecords, (g) => {
      return g?.GroupBy?.[groupBy] === groupByValue?.[0];
    });
  } else if (groupByValue === null) {
    // EMPTY group (Record was dragged to EMPTY group. Ex: "No Single Reference")
    i = findIndex(newRecords, (g) => {
      return g?.GroupBy?.[groupBy] === groupByValue;
    });
  } else if (isArray && !groupByValue.length) {
    // EMPTY MultiReference Group (Record was dragged to EMPTY group. Ex: "No Multi Reference")
    i = findIndex(newRecords, (g) => {
      return g?.GroupBy?.[groupBy] === null;
    });

    // Remove all instances of this record
    for (const recordGroup of newRecords) {
      recordGroup.GroupBy.records = recordGroup?.GroupBy?.records.filter(
        (r: any) => r._id !== record.id
      );
    }
  }

  if (i === undefined) return;

  if (i !== -1) {
    newRecords[i]?.GroupBy?.records?.push(record);
  } else {
    newRecords.push({
      GroupBy: {
        id: groupBy,
        Count: 1,
        records: [record],
        [groupBy]: record[groupBy] ?? null,
      },
      _ModuleAPIName: moduleAPIName,
    });
  }
};

/**
 * handleBulkUpdatedRecords - Handles the update of records.
 *
 * @param {*} queryClient - The React Query Client used to update the record caches
 * @param {*} moduleAPIName - The module API name that the records belong to
 * @param {*} records - The new records to update the cache with
 */
export const handleUpdatedRecords = (
  queryClient: QueryClient,
  moduleAPIName: string,
  records: any[],
  tenantID: string
) => {
  if (!Array.isArray(records)) records = [records];

  queryClient.setQueriesData(
    { queryKey: [tenantID, "records", moduleAPIName] },
    (prev: any) => {
      const noPrevRecords = !prev?.records;

      const first = prev?.records?.[0];

      // Grouped queries have a completely separate data structure.
      const isGroupedQuery =
        first?.hasOwnProperty("GroupBy") &&
        Array.isArray(first.GroupBy?.records) &&
        first?._ModuleAPIName === moduleAPIName;

      // Fixes issues with changes not appearing in Board views
      if (isGroupedQuery) prev = structuredClone(prev);

      let newRecords = noPrevRecords
        ? []
        : isGroupedQuery
        ? prev.records
        : [...prev.records];

      for (const record of records) {
        if (isGroupedQuery) {
          const groupBy = newRecords?.[0]?.GroupBy?.id;

          if (groupBy) {
            let found; // This ultimately ensures the record gets placed somewhere.

            for (const group of newRecords) {
              if (!group) continue;

              const groupRecords = group?.GroupBy?.records ?? [];

              // Find the record in the group
              const rowIndex = findIndex(groupRecords, { _id: record._id });

              if (rowIndex < 0) continue;
              else found = true;

              const existing = groupRecords[rowIndex];

              // Record doesn't exist in the Found group. It moved to another!
              if (
                JSON.stringify(existing[groupBy]) !==
                JSON.stringify(record[groupBy])
              ) {
                groupRecords.splice(rowIndex, 1);

                findGroupForRecord(newRecords, groupBy, record, moduleAPIName);
              } else {
                groupRecords[rowIndex] = record;
              }

              // Record has been moved. We can now move to the next record.
              if (found) break;
            }

            // Handle new record / record that didn't have a group prior
            if (!found && record[groupBy]) {
              findGroupForRecord(newRecords, groupBy, record, moduleAPIName);
            }
          }

          // README: Uncomment the below if requested, BUT make sure for Timelines it isn't run!

          // Filter out any empty groups as a result of the updates performed
          // newRecords = newRecords.filter(
          //   (g: any) => !!g?.GroupBy?.records?.length
          // );
        } else {
          let rowIndex = findIndex(newRecords, { id: record.id });

          if (rowIndex < 0) continue;

          newRecords[rowIndex] = record;
        }

        queryClient.setQueryData(
          [tenantID, "record", moduleAPIName, record.id],
          record
        );
      }

      return { ...(prev ?? {}), records: newRecords };
    }
  );
};

// Ensures table aggregations are updated when the record is updated. (If necessary)
export const handleAggregationUpdates = (
  queryClient: QueryClient,
  tenantID: string,
  moduleAPIName: string,
  payload: any
) => {
  const allQueries = queryClient.getQueriesData({
    queryKey: [tenantID, "records", moduleAPIName],
  });

  // Get all the Aggregation Queries.
  //Console log `allQueries` if you want to review this code.
  const aggregationQueries: {
    QueryKey: any[];
    Aggregations: { Field: string; Method: string }[];
  }[] = allQueries
    .filter(
      (q: any) =>
        !!q[1] &&
        !!Object.keys(q?.[0]?.[3] || {})?.length &&
        !!q?.[0]?.[3]?.Aggregations
    )
    .map((q: any) => ({
      Aggregations: q?.[0]?.[3]?.Aggregations,
      QueryKey: q?.[0],
    }));

  const payloadFields = Object.keys(payload);

  // Invalidate all Aggregation Queries that have the same fields as the payload.
  for (const aggData of aggregationQueries) {
    const agg = aggData?.Aggregations;

    if (!Array.isArray(agg)) continue;

    const aggQueryFields = agg?.map((a) => a?.Field);

    if (!intersection(payloadFields, aggQueryFields).length) continue;

    queryClient.invalidateQueries({
      queryKey: aggData?.QueryKey,
    });
  }
};

export const handleUpdateUserRecordRecommendedActivity = (
  userRecordActivityPayload: {
    [moduleAPIName: string]: { add: string[]; remove: string[] };
  },
  updateUserRecordActivityCache: ({
    userRecordActivities,
  }: {
    userRecordActivities: UserRecordActivityPayload[];
  }) => void
) => {
  const updatedFormat = map(userRecordActivityPayload, (value, key) => ({
    moduleAPIName: key,
    add: value.add,
    remove: value.remove,
    type: "Recent" as const,
  }));

  updateUserRecordActivityCache({ userRecordActivities: updatedFormat });
};
